import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar as CalIcon, Clock, ArrowLeft, ArrowRight, ShieldCheck, MapPin,
  User, Phone, CheckCircle2, Loader2, Sparkles, Gamepad2, Monitor, Plus,
  Trash2, ShoppingCart, Mail, Pencil,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { generateSlots, fmtTime, fmtDate, toDateStr } from "@/lib/slots";
import { setupIcon } from "@/components/booking/setupIcon";
import { LOGO_URL, HERO_URL, FEATURED_GAMES } from "@/lib/assets";

const BSTEPS = ["Date", "Platform", "Station", "Duration", "Time", "Add-ons"];
const STATUS_BADGE = {
  available: { text: "Available", cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
  maintenance: { text: "Under Maintenance", cls: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
  unavailable: { text: "Unavailable", cls: "text-red-400 border-red-500/40 bg-red-500/10" },
  booked: { text: "Fully Booked", cls: "text-red-400 border-red-500/40 bg-red-500/10" },
};
const emptyDraft = () => ({ date: new Date(), platform: null, station: null, duration: 60, startTime: null, setup: null });

export default function BookingPage() {
  const [config, setConfig] = useState(null);
  const [games, setGames] = useState([]);
  const [started, setStarted] = useState(false);
  const [view, setView] = useState("builder"); // builder | cart | details | payment | done
  const [bstep, setBstep] = useState(1);
  const [draft, setDraft] = useState(emptyDraft());
  const [avail, setAvail] = useState(null);
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState({ name: "", mobile: "", email: "", notes: "" });
  const [payMode, setPayMode] = useState(null);
  const [advance, setAdvance] = useState(null);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => toast.error("Failed to load café info"));
    api.get("/games").then((r) => setGames(r.data)).catch(() => {});
  }, []);

  const dateStr = toDateStr(draft.date);
  const probeSetup = (plat) => config?.setups.find((s) => s.station_type === plat && s.enabled);
  const platformSetups = (plat) => config?.setups.filter((s) => s.station_type === plat && s.enabled) || [];

  const loadAvail = async (plat, d) => {
    const probe = probeSetup(plat);
    const r = await api.get("/availability", { params: { date: toDateStr(d), setup_id: probe.id } });
    setAvail(r.data);
  };

  const cartIntervals = (dStr, stationId) => cart.filter((c) => c.dateStr === dStr && c.station.id === stationId)
    .map((c) => [c.startMin, c.endMin]);

  const stationSlots = (st, dur) => {
    if (!avail) return [];
    const merged = [...st.booked_intervals, ...cartIntervals(dateStr, st.id)];
    return generateSlots(avail.open_time, avail.close_time, avail.slot_interval, dur, merged);
  };
  const stationStatus = (st) => {
    if (st.status !== "available") return st.status;
    const merged = [...st.booked_intervals, ...cartIntervals(dateStr, st.id)];
    const slots = generateSlots(avail.open_time, avail.close_time, avail.slot_interval, avail.slot_interval, merged);
    return slots.some((s) => s.available) ? "available" : "booked";
  };

  const draftInterval = () => { if (!draft.startTime) return [0, 0]; const [h, m] = draft.startTime.split(":").map(Number); const s = h * 60 + m; return [s, s + draft.duration]; };
  const resourceConflict = (setup) => {
    const addons = setup.addons || [];
    if (!addons.length || !avail) return null;
    const [s, e] = draftInterval();
    for (const a of addons) {
      const cap = (avail.resources && avail.resources[a]) ?? 1;
      let used = 0;
      (avail.resource_intervals?.[a] || []).forEach(([bs, be]) => { if (s < be && bs < e) used++; });
      cart.filter((c) => c.dateStr === dateStr).forEach((c) => { if ((c.setup.addons || []).includes(a) && s < c.endMin && c.startMin < e) used++; });
      if (used >= cap) return a === "vr" ? "VR headset" : "Steering wheel";
    }
    return null;
  };

  const startBuilder = () => { setDraft(emptyDraft()); setBstep(1); setView("builder"); setStarted(true); };

  const choosePlatform = async (plat) => {
    setDraft((d) => ({ ...d, platform: plat, station: null, startTime: null, setup: null }));
    await loadAvail(plat, draft.date);
    setBstep(3);
  };

  const endMinOf = (startTime, dur) => { const [h, m] = startTime.split(":").map(Number); return h * 60 + m + dur; };

  const finalizeSlot = (setup) => {
    const startMin = (() => { const [h, m] = draft.startTime.split(":").map(Number); return h * 60 + m; })();
    const endMin = startMin + draft.duration;
    const item = {
      key: Math.random().toString(36).slice(2),
      dateStr, dateObj: draft.date, platform: draft.platform,
      station: draft.station, setup, duration: draft.duration,
      startTime: draft.startTime, startMin, endMin,
      endTime: `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`,
      amount: setup.prices[String(draft.duration)],
      durationLabel: config.durations.find((x) => x.minutes === draft.duration)?.label || `${draft.duration} min`,
    };
    setCart((c) => [...c, item]);
    setView("cart");
    toast.success("Slot added to cart");
  };

  const removeSlot = (key) => setCart((c) => c.filter((x) => x.key !== key));
  const total = cart.reduce((s, c) => s + c.amount, 0);
  const allPC = cart.length > 0 && cart.every((c) => c.platform === "pc");
  const minAdv = cart.reduce((s, c) => s + (c.platform === "ps5" ? 50 : 20), 0);
  const advVal = Math.max(minAdv, Math.min(total, advance ?? minAdv));
  const payuEnabled = config?.payment?.payu_enabled !== false;
  const cashEnabled = config?.payment?.cash_enabled !== false;

  const confirm = async () => {
    if (!payMode) return;
    setLoading(true);
    try {
      const r = await api.post("/bookings/batch", {
        slots: cart.map((c) => ({ date: c.dateStr, setup_id: c.setup.id, station_id: c.station.id, start_time: c.startTime, duration_minutes: c.duration })),
        customer: { customer_name: customer.name, mobile: customer.mobile, email: customer.email, notes: customer.notes, payment_method: payMode === "cash" ? "cash" : "payu", advance_amount: payMode === "advance" ? advVal : 0 },
        otp: payMode === "cash" ? otp : undefined,
        pay_mode: payMode,
      });
      if (payMode === "cash") {
        setResult(r.data);
        setView("done");
        if (r.data.whatsapp_url) window.open(r.data.whatsapp_url, "_blank");
      } else {
        window.location.assign(`${process.env.REACT_APP_BACKEND_URL}/api/payu/start/${r.data.group_id}`);
      }
    } catch (e) { toast.error(apiError(e)); setLoading(false); }
  };

  const resetAll = () => { setResult(null); setCart([]); setCustomer({ name: "", mobile: "", email: "", notes: "" }); setPayMode(null); setAdvance(null); setOtp(""); setOtpSent(false); startBuilder(); };

  if (!config) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-purple-400" size={40} /></div>;

  return (
    <div className="min-h-screen text-slate-100">
      <Nav cartCount={cart.length} onCart={() => started && setView("cart")} />
      {!started && !result ? (
        <Landing config={config} games={games} onStart={startBuilder} />
      ) : (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 pb-16">
          <AnimatePresence mode="wait">
            <motion.div key={view + bstep} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.28 }}>

              {view === "builder" && (
                <>
                  <div className="flex items-center gap-1.5 mb-6 overflow-x-auto pb-1">
                    {BSTEPS.map((s, i) => (
                      <div key={s} className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider ${bstep === i + 1 ? "bg-purple-600 text-white" : bstep > i + 1 ? "text-emerald-400" : "text-slate-500"}`}>{i + 1}. {s}</div>
                    ))}
                  </div>

                  {bstep === 1 && (
                    <StepCard title="Select Date" sub="Choose the day for this slot" icon={CalIcon}>
                      <div className="flex justify-center"><div className="glass rounded-2xl p-2">
                        <Calendar mode="single" selected={draft.date} onSelect={(d) => d && setDraft((x) => ({ ...x, date: d }))} disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }} data-testid="date-calendar" />
                      </div></div>
                      <p className="text-center mt-4 text-sm text-purple-300 font-mono">{fmtDate(dateStr)}</p>
                      <NavButtons onNext={() => setBstep(2)} nextTestId="date-next-btn" />
                    </StepCard>
                  )}

                  {bstep === 2 && (
                    <StepCard title="Select Platform" sub="Which gaming platform?" icon={Gamepad2}>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {["pc", "ps5"].map((p) => (
                          <button key={p} data-testid={`select-platform-${p}`} onClick={() => choosePlatform(p)} className="glass rounded-2xl p-6 text-center hover:border-purple-500/60 hover:-translate-y-0.5 transition-all">
                            {p === "pc" ? <Monitor className="mx-auto text-purple-300 mb-2" size={32} /> : <Gamepad2 className="mx-auto text-purple-300 mb-2" size={32} />}
                            <h3 className="font-display font-bold text-lg tracking-wide">{p === "pc" ? "PC GAMING" : "PS5 CONSOLE"}</h3>
                            <p className="text-xs text-slate-400 mt-1">{p === "pc" ? "4 High-end PC rigs" : "3 Next-gen PS5 stations"}</p>
                          </button>
                        ))}
                      </div>
                      <NavButtons onBack={() => setBstep(1)} />
                    </StepCard>
                  )}

                  {bstep === 3 && avail && (
                    <StepCard title="Select Station" sub={`${draft.platform.toUpperCase()} • ${fmtDate(dateStr)}`} icon={ShieldCheck}>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {avail.stations.map((st) => {
                          const status = stationStatus(st); const badge = STATUS_BADGE[status]; const disabled = status !== "available";
                          return (
                            <button key={st.id} data-testid={`select-station-${st.id}`} disabled={disabled} onClick={() => { setDraft((d) => ({ ...d, station: st, startTime: null })); setBstep(4); }}
                              className={`text-left glass rounded-2xl p-4 flex items-center justify-between transition-all ${disabled ? "opacity-55 cursor-not-allowed" : "hover:border-purple-500/60 hover:-translate-y-0.5"}`}>
                              <span className="font-semibold">{st.name}</span>
                              <span className={`text-[11px] px-2.5 py-1 rounded-full border font-mono ${badge.cls}`}>{badge.text}</span>
                            </button>
                          );
                        })}
                      </div>
                      <NavButtons onBack={() => setBstep(2)} />
                    </StepCard>
                  )}

                  {bstep === 4 && (
                    <StepCard title="Select Duration" sub={draft.station.name} icon={Clock}>
                      <div className="flex flex-wrap gap-3">
                        {config.durations.map((d) => (
                          <button key={d.minutes} data-testid={`select-duration-${d.minutes}`} onClick={() => { setDraft((x) => ({ ...x, duration: d.minutes, startTime: null })); setBstep(5); }}
                            className={`px-5 h-12 rounded-xl border font-semibold transition-all ${draft.duration === d.minutes ? "bg-purple-600 border-purple-400 text-white" : "glass text-slate-300 hover:border-purple-500/50"}`}>{d.label}</button>
                        ))}
                      </div>
                      <NavButtons onBack={() => setBstep(3)} />
                    </StepCard>
                  )}

                  {bstep === 5 && (
                    <StepCard title="Select Start Time" sub={`${draft.station.name} • ${config.durations.find((d) => d.minutes === draft.duration)?.label}`} icon={Clock}>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                        {stationSlots(draft.station, draft.duration).map((s) => (
                          <button key={s.start} data-testid={`select-slot-${s.start}`} disabled={!s.available} onClick={() => { setDraft((x) => ({ ...x, startTime: s.start })); setBstep(6); }}
                            className={`h-11 rounded-xl border text-sm font-medium font-mono transition-all ${!s.available ? "opacity-30 line-through cursor-not-allowed border-white/10" : "glass text-slate-200 hover:border-purple-500/50"}`}>{fmtTime(s.start)}</button>
                        ))}
                      </div>
                      <NavButtons onBack={() => setBstep(4)} />
                    </StepCard>
                  )}

                  {bstep === 6 && (
                    <StepCard title={draft.platform === "ps5" ? "Select Setup & Add-ons" : "Confirm Setup"} sub={`${draft.station.name} • ${fmtTime(draft.startTime)}`} icon={Gamepad2}>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {platformSetups(draft.platform).map((s) => {
                          const Icon = setupIcon(s.id);
                          const conflict = resourceConflict(s);
                          return (
                            <button key={s.id} data-testid={`select-setup-${s.id}`} disabled={!!conflict} onClick={() => finalizeSlot(s)} className={`text-left glass rounded-2xl p-4 transition-all ${conflict ? "opacity-50 cursor-not-allowed" : "hover:border-purple-500/60 hover:-translate-y-0.5"}`}>
                              <div className="flex items-center gap-2 mb-1"><Icon className="text-purple-300" size={18} /><h3 className="font-bold">{s.name}</h3></div>
                              <p className="text-xs text-slate-400 mb-2 min-h-[2rem]">{s.desc}</p>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-emerald-400 font-mono">₹{s.prices[String(draft.duration)]}</span>
                                {conflict && <span className="text-[10px] text-amber-400" data-testid={`conflict-${s.id}`}>{conflict} in use</span>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      <NavButtons onBack={() => setBstep(5)} />
                    </StepCard>
                  )}
                </>
              )}

              {view === "cart" && (
                <StepCard title="Your Booking Cart" sub={`${cart.length} slot(s)`} icon={ShoppingCart}>
                  {cart.length === 0 ? <p className="text-slate-500 text-center py-6">Cart is empty.</p> : (
                    <div className="space-y-3">
                      {cart.map((c, i) => (
                        <div key={c.key} className="glass rounded-2xl p-4 flex justify-between items-start" data-testid={`cart-item-${i}`}>
                          <div>
                            <p className="font-semibold">{c.station.name} · {c.setup.name}</p>
                            <p className="text-sm text-slate-400">{fmtDate(c.dateStr)}</p>
                            <p className="text-sm text-slate-400 font-mono">{fmtTime(c.startTime)}–{fmtTime(c.endTime)} · {c.durationLabel}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-display text-lg font-bold text-emerald-400">₹{c.amount}</p>
                            <button data-testid={`remove-slot-${i}`} onClick={() => removeSlot(c.key)} className="text-red-300 text-xs flex items-center gap-1 mt-1 ml-auto"><Trash2 size={13} /> Remove</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="glass rounded-2xl p-4 mt-4 flex justify-between items-center">
                    <span className="uppercase tracking-wider text-slate-400 text-sm">Total</span>
                    <span className="font-display text-2xl font-bold text-emerald-400" data-testid="cart-total">₹{total}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 mt-5">
                    <Button variant="outline" data-testid="add-another-slot-btn" onClick={startBuilder} className="h-12 border-purple-500/30 bg-transparent hover:bg-purple-950/40"><Plus size={16} /> Add Another Slot</Button>
                    <Button data-testid="continue-details-btn" disabled={cart.length === 0} onClick={() => setView("details")} className="h-12 flex-1 bg-purple-600 hover:bg-purple-500 font-semibold">Continue to Details <ArrowRight size={16} /></Button>
                  </div>
                </StepCard>
              )}

              {view === "details" && (
                <StepCard title="Customer Details" sub="Tell us who's playing" icon={User}>
                  <div className="space-y-4">
                    <Field icon={User} label="Full Name" testid="input-name" value={customer.name} onChange={(v) => setCustomer({ ...customer, name: v })} placeholder="Enter your full name" />
                    <Field icon={Phone} label="Mobile Number (WhatsApp)" testid="input-mobile" value={customer.mobile} onChange={(v) => setCustomer({ ...customer, mobile: v.replace(/[^0-9+]/g, "") })} placeholder="e.g. 9876543210" />
                    <Field icon={Mail} label="Email (optional)" testid="input-email" value={customer.email} onChange={(v) => setCustomer({ ...customer, email: v })} placeholder="you@email.com" />
                    <div>
                      <label className="text-xs uppercase tracking-wider text-purple-400 font-mono">Special Notes (optional)</label>
                      <textarea data-testid="input-notes" value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} rows={2} className="mt-1.5 w-full rounded-xl bg-purple-950/30 border border-purple-500/30 p-3 text-sm" placeholder="Any requests?" />
                    </div>
                  </div>
                  <NavButtons onBack={() => setView("cart")} onNext={(customer.name.trim() && customer.mobile.trim().length >= 10) ? () => setView("payment") : null} nextLabel="Continue to Payment" nextTestId="details-next-btn" />
                </StepCard>
              )}

              {view === "payment" && (
                <StepCard title="Payment" sub="Choose how you'd like to pay" icon={CheckCircle2}>
                  <div className="glass rounded-2xl p-4 mb-4 space-y-1">
                    <div className="flex justify-between text-sm"><span className="text-slate-400">Total Amount</span><span className="font-mono text-emerald-400" data-testid="pay-total">₹{total}</span></div>
                    {payMode === "advance" && <>
                      <div className="flex justify-between text-sm"><span className="text-slate-400">Advance to Pay</span><span className="font-mono text-purple-300" data-testid="pay-advance">₹{advVal}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-slate-400">Remaining (at café)</span><span className="font-mono text-amber-400" data-testid="pay-remaining">₹{total - advVal}</span></div>
                    </>}
                  </div>

                  <div className="grid sm:grid-cols-3 gap-3">
                    {payuEnabled && <PayCard id="full" title="Pay Full" desc={`Pay ₹${total} online now`} active={payMode === "full"} onClick={() => setPayMode("full")} />}
                    {payuEnabled && <PayCard id="advance" title="Pay Advance" desc={`From ₹${minAdv} online, rest at café`} active={payMode === "advance"} onClick={() => setPayMode("advance")} />}
                    {cashEnabled && <PayCard id="cash" title="Pay Cash at Café" desc="Reserve now, pay at counter" active={payMode === "cash"} onClick={() => setPayMode("cash")} />}
                  </div>

                  {payMode === "advance" && (
                    <div className="glass rounded-2xl p-4 mt-4">
                      <label className="text-xs uppercase tracking-wider text-purple-400 font-mono">Advance amount (min ₹{minAdv}, max ₹{total})</label>
                      <Input data-testid="advance-input" type="number" min={minAdv} max={total} value={advance ?? minAdv} onChange={(e) => setAdvance(Number(e.target.value))} className="h-11 mt-1.5 bg-purple-950/30 border-purple-500/30 font-mono" />
                      <div className="flex flex-wrap gap-2 mt-2">
                        {[...new Set([minAdv, Math.max(minAdv, Math.round(total * 0.5)), total])].filter((v) => v >= minAdv && v <= total).map((v) => (
                          <button key={v} data-testid={`adv-quick-${v}`} onClick={() => setAdvance(v)} className={`px-3 h-9 rounded-lg text-sm border ${advVal === v ? "bg-purple-600 border-purple-400" : "glass border-purple-500/30 text-slate-300"}`}>₹{v}{v === total ? " (Full)" : ""}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {payMode === "cash" && (
                    <div className="glass rounded-2xl p-4 mt-4">
                      <p className="text-xs uppercase tracking-[0.2em] text-purple-400 font-mono mb-3">Verify Your Email</p>
                      <div className="flex gap-2">
                        <Input data-testid="otp-email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} placeholder="you@email.com" className="h-11 flex-1 bg-purple-950/30 border-purple-500/30" />
                        <Button data-testid="otp-send-btn" disabled={sending || !customer.email.includes("@")} onClick={async () => { setSending(true); try { await api.post("/otp/send", { email: customer.email }); setOtpSent(true); toast.success("Code sent to your email"); } catch (e) { toast.error(apiError(e)); } finally { setSending(false); } }} className="h-11 bg-purple-600 hover:bg-purple-500">{sending ? <Loader2 className="animate-spin" /> : otpSent ? "Resend" : "Send Code"}</Button>
                      </div>
                      {otpSent && <Input data-testid="otp-code" value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} placeholder="Enter 6-digit code" className="h-11 mt-3 bg-purple-950/30 border-purple-500/30 font-mono tracking-widest" />}
                      <p className="text-[11px] text-slate-500 mt-2">Email verification is required only for pay-at-café bookings.</p>
                    </div>
                  )}

                  {(() => {
                    const canSubmit = payMode === "full" ? true : payMode === "advance" ? (advVal >= minAdv && advVal <= total) : payMode === "cash" ? (customer.email.includes("@") && otp.length === 6) : false;
                    const label = payMode === "full" ? `PAY ₹${total} ONLINE` : payMode === "advance" ? `PAY ₹${advVal} ONLINE` : payMode === "cash" ? "RESERVE SLOT" : "SELECT AN OPTION";
                    return (
                      <Button data-testid="confirm-booking-btn" disabled={loading || !canSubmit} onClick={confirm} className="w-full h-14 mt-5 text-base font-bold bg-purple-600 hover:bg-purple-500 pulse-glow">
                        {loading ? <Loader2 className="animate-spin" /> : <>{label} <ArrowRight size={18} /></>}
                      </Button>
                    );
                  })()}
                  <button onClick={() => setView("details")} className="w-full text-center text-sm text-slate-500 mt-3">Back</button>
                </StepCard>
              )}

              {view === "done" && result && <Confirmation result={result} onReset={resetAll} />}
            </motion.div>
          </AnimatePresence>
        </div>
      )}
      <Footer config={config} />
    </div>
  );
}

function Field({ icon: Icon, label, testid, value, onChange, placeholder }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-purple-400 font-mono">{label}</label>
      <div className="relative mt-1.5">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
        <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-10 h-12 bg-purple-950/30 border-purple-500/30" />
      </div>
    </div>
  );
}

function PayCard({ id, title, desc, active, onClick }) {
  return (
    <button data-testid={`pay-${id}`} onClick={onClick} className={`glass rounded-2xl p-5 text-left transition-all ${active ? "border-purple-500 neon-border" : "hover:border-purple-500/50"}`}>
      <h3 className="font-display font-bold">{title}</h3>
      <p className="text-sm text-slate-400">{desc}</p>
    </button>
  );
}

function Nav({ cartCount, onCart }) {
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-purple-950/50 border-b border-purple-500/20">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2" data-testid="logo-home">
          <img src={LOGO_URL} alt="Play Station" className="h-10 w-10 rounded-lg object-cover" />
          <span className="font-display font-bold tracking-wider text-sm sm:text-base">PLAY STATION</span>
        </a>
        <div className="flex items-center gap-4">
          {cartCount > 0 && <button onClick={onCart} data-testid="nav-cart" className="relative text-slate-300 hover:text-purple-300"><ShoppingCart size={20} /><span className="absolute -top-2 -right-2 bg-purple-600 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{cartCount}</span></button>}
          <Link to="/admin" data-testid="admin-link" className="text-xs sm:text-sm text-slate-400 hover:text-purple-300 flex items-center gap-1.5"><ShieldCheck size={16} /> Admin</Link>
        </div>
      </div>
    </header>
  );
}

function Landing({ config, games, onStart }) {
  return (
    <div>
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <img src={HERO_URL} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,5,18,0.75) 0%, rgba(10,5,18,0.96) 100%)" }} />
        <div className="relative z-10 text-center px-5 pt-20">
          <motion.img initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} src={LOGO_URL} alt="Play Station Gaming Café" className="w-40 h-40 sm:w-52 sm:h-52 mx-auto rounded-3xl object-cover mb-4" />
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight uppercase neon-text">Play Station</motion.h1>
          <p className="font-display text-lg sm:text-xl text-purple-300 tracking-[0.3em] uppercase mt-1">Gaming Café</p>
          <p className="mt-4 text-slate-300 flex items-center justify-center gap-2 text-sm"><MapPin size={15} /> {config.location}</p>
          <p className="mt-5 font-display text-xl sm:text-2xl tracking-wide">PLAY. COMPETE. CHILL.</p>
          <p className="text-slate-400 text-sm">Your game. Your space. Your time.</p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
            <Button data-testid="book-slot-btn" onClick={onStart} className="mt-8 h-14 px-10 text-base font-bold bg-purple-600 hover:bg-purple-500 pulse-glow rounded-full"><Sparkles size={18} /> BOOK YOUR GAMING SLOT</Button>
          </motion.div>
          <p className="mt-6 text-xs text-slate-500 font-mono uppercase tracking-wider">Open {fmtTime(config.open_time)} – {fmtTime(config.close_time)} · 7 Days a Week</p>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-4 py-14">
        <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase text-center mb-8">Charges & Packages</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {config.setups.map((s) => {
            const Icon = setupIcon(s.id);
            return (
              <div key={s.id} className="glass rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4"><Icon className="text-purple-300" size={20} /><h3 className="font-display font-bold tracking-wide">{s.name}</h3></div>
                {config.durations.map((d) => (
                  <div key={d.minutes} className="flex justify-between py-1.5 border-b border-purple-500/10 text-sm"><span className="text-slate-400">{d.label}</span><span className="font-mono text-purple-200">₹{s.prices[String(d.minutes)]}</span></div>
                ))}
              </div>
            );
          })}
        </div>
        <div className="mt-10 flex flex-wrap justify-center gap-2">{FEATURED_GAMES.map((g) => <span key={g} className="px-3 py-1.5 rounded-full glass text-xs text-purple-200 font-mono">{g}</span>)}</div>
      </section>
      <GameGallery games={games} />
    </div>
  );
}

function GameGallery({ games }) {
  const [pf, setPf] = useState("all");
  if (!games || games.length === 0) return null;
  const shown = games.filter((g) => pf === "all" || g.platform === pf);
  return (
    <section className="max-w-6xl mx-auto px-4 pb-16" data-testid="game-gallery">
      <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase text-center mb-2">Game Gallery</h2>
      <p className="text-center text-slate-400 text-sm mb-6">Blockbusters ready to play on our PC & PS5 stations</p>
      <div className="flex justify-center gap-2 mb-6">
        {[["all", "All"], ["pc", "PC"], ["ps5", "PS5"]].map(([id, l]) => (
          <button key={id} data-testid={`gallery-filter-${id}`} onClick={() => setPf(id)} className={`px-4 h-9 rounded-full text-sm border transition-all ${pf === id ? "bg-purple-600 border-purple-400" : "glass border-purple-500/30 text-slate-300"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {shown.map((g) => (
          <div key={g.id} data-testid={`game-${g.id}`} className="group relative rounded-2xl overflow-hidden glass aspect-[3/4]">
            {g.image_url
              ? <img src={g.image_url} alt={g.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              : <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 20%, rgba(168,85,247,0.5), transparent 60%), linear-gradient(160deg,#1a0f2e,#0a0512)" }} />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
            <div className="absolute top-2 right-2 text-[10px] font-mono px-2 py-0.5 rounded-full bg-purple-600/70 uppercase">{g.platform}</div>
            {!g.image_url && <div className="absolute inset-0 flex items-center justify-center">{g.platform === "pc" ? <Monitor className="text-purple-300/40" size={44} /> : <Gamepad2 className="text-purple-300/40" size={44} />}</div>}
            <p className="absolute bottom-3 left-3 right-3 font-display font-bold text-sm leading-tight">{g.name}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function StepCard({ title, sub, icon: Icon, children }) {
  return (
    <div className="glass rounded-3xl p-6 sm:p-8 animate-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-purple-600/20 border border-purple-500/40 flex items-center justify-center"><Icon className="text-purple-300" size={22} /></div>
        <div><h2 className="font-display text-xl sm:text-2xl font-bold tracking-wide">{title}</h2><p className="text-sm text-slate-400">{sub}</p></div>
      </div>
      {children}
    </div>
  );
}

function NavButtons({ onBack, onNext, nextLabel = "Continue", nextTestId }) {
  return (
    <div className="flex gap-3 mt-7">
      {onBack && <Button variant="outline" onClick={onBack} className="h-12 border-purple-500/30 bg-transparent hover:bg-purple-950/40"><ArrowLeft size={16} /> Back</Button>}
      {onNext !== undefined && <Button data-testid={nextTestId} onClick={onNext} disabled={!onNext} className="h-12 flex-1 bg-purple-600 hover:bg-purple-500 font-semibold">{nextLabel} <ArrowRight size={16} /></Button>}
    </div>
  );
}

function Confirmation({ result, onReset }) {
  return (
    <div className="glass rounded-3xl p-6 sm:p-8 text-center animate-in" data-testid="confirmation-screen">
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }} className="mx-auto w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mb-4"><CheckCircle2 className="text-emerald-400" size={36} /></motion.div>
      <p className="font-display text-sm text-purple-300 uppercase tracking-widest">🎮 Play Station Gaming Café</p>
      <h2 className="font-display text-2xl sm:text-3xl font-bold mt-1 neon-text">Booking Reserved!</h2>
      <p className="text-sm text-amber-300 mt-1">Your slot is held. Please arrive on time and pay at the café counter. The café may call to confirm.</p>
      <p className="text-sm text-slate-400 mt-1">Order ID: <span className="font-mono text-purple-200" data-testid="group-id">{result.group_id}</span></p>
      <div className="space-y-3 mt-6 text-left">
        {result.bookings.map((b) => (
          <div key={b.id} className="glass rounded-2xl p-4" data-testid={`confirmed-${b.booking_id}`}>
            <div className="flex justify-between items-center mb-2"><span className="font-mono text-purple-300 text-sm">{b.booking_id}</span><span className="font-display font-bold text-emerald-400">₹{b.amount}</span></div>
            <p className="font-semibold">{b.station_name} · {b.setup_name}</p>
            <p className="text-sm text-slate-400">{fmtDate(b.date)} · {fmtTime(b.start_time)}–{fmtTime(b.end_time)} · {b.duration_label}</p>
          </div>
        ))}
      </div>
      <div className="glass rounded-2xl divide-y divide-purple-500/10 mt-4">
        <div className="flex justify-between px-4 py-3"><span className="text-slate-400 uppercase text-sm tracking-wider">Grand Total</span><span className="font-display text-xl font-bold text-emerald-400">₹{result.total}</span></div>
        <div className="flex justify-between px-4 py-2.5 text-sm"><span className="text-slate-400">Advance Paid</span><span className="font-mono text-emerald-400">₹{result.advance_paid}</span></div>
        <div className="flex justify-between px-4 py-2.5 text-sm"><span className="text-slate-400">Remaining (pay at café)</span><span className="font-mono text-amber-400">₹{result.remaining}</span></div>
      </div>
      {result.whatsapp_url && <a href={result.whatsapp_url} target="_blank" rel="noreferrer" data-testid="whatsapp-notify-btn"><Button className="w-full h-12 mt-5 bg-emerald-600 hover:bg-emerald-500 font-semibold"><Phone size={16} /> Notify Café on WhatsApp</Button></a>}
      <Button data-testid="book-another-btn" onClick={onReset} variant="outline" className="w-full h-12 mt-3 border-purple-500/30 bg-transparent hover:bg-purple-950/40">New Booking</Button>
    </div>
  );
}

function Footer({ config }) {
  return (
    <footer className="border-t border-purple-500/15 py-8 text-center text-sm text-slate-500">
      <p className="font-display tracking-wider text-purple-300">PLAY STATION GAMING CAFÉ</p>
      <p className="mt-1">{config?.location} · Open {config && fmtTime(config.open_time)}–{config && fmtTime(config.close_time)}</p>
    </footer>
  );
}
